/**
 * Button — React (web) implementation.
 *
 * All styling references the shared variant maps in Button.ts,
 * which in turn reference the colour/typography elements.
 * No raw hex values or pixel font sizes in this file.
 */

import React from 'react';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import type { ButtonProps, ButtonType, WebButtonWidth } from './Button';
import {
  iconOnlyDimensions,
  textButtonHeight,
  LARGE_BUTTON_MAX_WIDTH,
  horizontalPaddingForSize,
  iconTextGapForSize,
  iconSizeForSize,
  backgroundForVariant,
  pressedBackgroundForVariant,
  textColorForVariant,
  typographyForSize,
  buttonSpinnerSizeForSize,
  buttonSpinnerToneForType,
  BUTTON_DEFAULT_ICON_POSITION,
} from './Button';
import { Icon } from '../Icon/Icon.web';
import { Spinner } from '../Spinner/Spinner.web';
import { useDelegatedAnalyticsAction } from '../../analytics/AnalyticsActionContext';

export interface WebButtonProps extends Omit<ButtonProps, 'width'> {
  /** `fill` and `hug` as on native, plus web-only `stretch`: full width with no Large cap. */
  width?: WebButtonWidth;
  onPress?: () => void;
  /** The `<button type>`; `submit` lets a form's primary action submit on Enter. */
  htmlType?: 'button' | 'submit';
  'aria-label'?: string;
  'data-testid'?: string;
}

const BUTTON_FOCUS_STYLE_ID = 'galapagos-button-focus-visible';
const BUTTON_FOCUS_SELECTOR = '[data-galapagos-button="true"]';

// Web-only pointer-hover fills, per type. Native has no hover. A type with no
// entry keeps its fill on hover: Brand and Critical have no hover design.
const hoverBackgroundForType: Partial<Record<ButtonType, string>> = {
  Primary: colors.button.primaryHover,
  Secondary: colors.button.disabled,
};

export function Button({
  size = 'L',
  type = 'Primary',
  state = 'Default',
  muted = false,
  icon = false,
  text = true,
  label,
  iconName,
  iconPosition = BUTTON_DEFAULT_ICON_POSITION,
  iconSize,
  width,
  accessibilityLabel,
  accessibilityHint,
  onPress,
  htmlType = 'button',
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  actionId,
}: WebButtonProps) {
  ensureFocusVisibleStyle(BUTTON_FOCUS_STYLE_ID, [BUTTON_FOCUS_SELECTOR]);
  const [isPressed, setIsPressed] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const isIconOnly = icon && !text;
  const isDisabled = state === 'Disabled';
  const isLoading = state === 'Loading';
  const isSelected = state === 'Selected';
  const isPressDisabled = isDisabled || isLoading;
  const delegatedAction = useDelegatedAnalyticsAction(actionId);
  // Hover covers the v7 web sizes only; S and M/L keep today's look.
  const hasHover = state === 'Default' && !muted && (size === 'L' || size === 'M');
  const hoverBg = hasHover ? hoverBackgroundForType[type] : undefined;
  const visualState = muted && state === 'Default' ? 'Disabled' : state;
  const bg = (isHovered && hoverBg) || backgroundForVariant[type][visualState];
  const fg = muted && state === 'Default' ? colors.text.primary : textColorForVariant[type][state];
  const typo = typographyForSize[size];
  const resolvedWidth = width ?? (size === 'L' ? 'fill' : 'hug');

  const dimensions = isIconOnly
    ? iconOnlyDimensions[size]
    : {
        width: resolvedWidth === 'hug' ? undefined : '100%',
        height: textButtonHeight[size],
      };

  const horizontalPadding = isIconOnly
    ? 0
    : icon && text
      ? horizontalPaddingForSize[size].iconText
      : horizontalPaddingForSize[size].textOnly;

  // `stretch` fills the parent with no cap, so it needs no centring wrapper.
  const isClamped = !isIconOnly && size === 'L' && resolvedWidth === 'fill';

  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: icon && text && !isLoading ? iconTextGapForSize[size] : 0,
    backgroundColor:
      isPressed && !isPressDisabled ? pressedBackgroundForVariant[type][visualState] : bg,
    color: fg,
    border: 'none',
    borderRadius: radius.rounded,
    fontFamily: typo.fontFamily,
    fontWeight: typo.fontWeight,
    fontSize: typo.fontSize,
    lineHeight: `${typo.lineHeight}px`,
    letterSpacing: typo.letterSpacing,
    paddingLeft: horizontalPadding,
    paddingRight: horizontalPadding,
    paddingTop: 0,
    paddingBottom: 0,
    cursor: isPressDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 1 : undefined,
    width: dimensions.width,
    height: dimensions.height,
    maxWidth: isClamped ? LARGE_BUTTON_MAX_WIDTH : undefined,
    minWidth: isIconOnly ? dimensions.width : undefined,
    overflow: 'hidden',
  };

  const buttonElement = (
    /* analytics-exempt: forwarded-wrapper */
    <button
      type={htmlType}
      style={style}
      onClick={isPressDisabled ? undefined : onPress}
      // An inert button still cancels the click, so a submit button never submits.
      onClickCapture={isPressDisabled ? (event) => event.preventDefault() : undefined}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onPointerEnter={(event) => {
        // Mouse only: a tap fires a pointer-enter with no matching leave.
        if (event.pointerType === 'mouse') setIsHovered(true);
      }}
      onPointerLeave={() => setIsHovered(false)}
      onMouseLeave={() => setIsPressed(false)}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
      aria-label={ariaLabel ?? accessibilityLabel ?? label}
      title={muted ? accessibilityHint : undefined}
      aria-busy={isLoading || undefined}
      aria-pressed={isSelected || undefined}
      aria-disabled={isPressDisabled || undefined}
      data-galapagos-button="true"
      data-analytics-action={delegatedAction}
      data-testid={dataTestId}
    >
      {isLoading && (
        <Spinner
          tone={buttonSpinnerToneForType[type]}
          size={buttonSpinnerSizeForSize[size]}
          data-testid={dataTestId ? `${dataTestId}-loading-indicator` : undefined}
        />
      )}
      {!isLoading && icon && iconName && iconPosition === 'leading' && (
        <Icon name={iconName} size={iconSize ?? iconSizeForSize[size]} color={fg} aria-hidden />
      )}
      {!isLoading && text && label && <span>{label}</span>}
      {!isLoading && icon && iconName && iconPosition === 'trailing' && (
        <Icon name={iconName} size={iconSize ?? iconSizeForSize[size]} color={fg} aria-hidden />
      )}
    </button>
  );

  // The clamped full-width button centers inside a component-owned wrapper
  // (internal justifyContent — ADR-020 Rule 2 keeps margin/alignSelf off the
  // root), so parents wider than the clamp show even spacing on both sides
  // instead of pinning the button to the leading edge.
  return isClamped ? (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{buttonElement}</div>
  ) : (
    buttonElement
  );
}
